import React, { createContext, useRef, ReactNode, useCallback } from 'react'
import { ExcelExportParams, CsvExportParams, exportMultipleSheetsAsExcel, GridApi } from 'ag-grid-enterprise'
import { Layout } from 'react-grid-layout'
import sanitizeFilename from '../../../../../utils/sanitizeFilename'
import { CellExportMetadataProvider } from '../../../../../shared/utils/excelJsExport/excelExportTypes'
import { exportCustomReportToExcel, ExcelExportSheetInput } from '../../../../../shared/utils/excelJsExport/excelExport'

export interface GridInfo {
  api: GridApi
  sheetName: string // Nimi Excel-sheetille
  exportOptions?: Partial<ExcelExportParams & CsvExportParams> // Lisäasetukset exportille
  /**
   * Optional per-cell metadata provider enabling the ExcelJS-based export
   * path with dynamic number formatting, colors and fonts. When provided,
   * `exportAllToExcel` and `exportSingleExcel` use ExcelJS instead of
   * ag-Grid's built-in exporter.
   */
  getCellMetadata?: CellExportMetadataProvider
}

export interface GridExportContextType {
  registerGrid: (id: number, gridInfo: GridInfo) => void
  unregisterGrid: (id: number) => void
  exportAllToExcel: (fileName?: string, layout?: Layout[]) => Promise<void>
  exportSingleExcel: (gridId: number, fileName?: string) => Promise<void>
  exportSingleCSV: (gridId: number, fileName?: string) => void
  getRegisteredGrids: () => number[]
  getGridInfo: (gridId: number) => GridInfo | undefined
  isExporting: boolean
  onExportError?: (error: string) => void
  // Grid registration status
  setExpectedCount: (count: number) => void
  expectedCount: number
  registeredCount: number
  allRegistered: boolean
}

const defaultExcelOptions: ExcelExportParams = {
  skipColumnGroupHeaders: false,
  skipColumnHeaders: false,
  allColumns: false,
  onlySelected: false,
  processGroupHeaderCallback(params) {
    return params.api.getDisplayNameForColumnGroup(params.columnGroup, null)
  },
  processHeaderCallback(params) {
    return params.api.getDisplayNameForColumn(params.column, null)
  },
  processCellCallback(params) {
    return params.value
  }
}

const defaultCsvOptions: CsvExportParams = {
  skipColumnGroupHeaders: false,
  skipColumnHeaders: false,
  allColumns: false,
  onlySelected: false,
  processGroupHeaderCallback(params) {
    return params.api.getDisplayNameForColumnGroup(params.columnGroup, null)
  },
  processHeaderCallback(params) {
    return params.api.getDisplayNameForColumn(params.column, null)
  },
  processCellCallback(params) {
    return params.value
  }
}

export const GridExportContext = createContext<GridExportContextType | null>(null)

export const GridExportProvider: React.FC<{
  children: ReactNode
  onExportError?: (error: string) => void
  expectedGridCount?: number
}> = ({ children, onExportError, expectedGridCount = 0 }) => {
  const gridsRef = useRef<Map<number, GridInfo>>(new Map())
  const [isExporting, setIsExporting] = React.useState(false)
  const [expectedCount, setExpectedCount] = React.useState(expectedGridCount)
  const [registeredCount, setRegisteredCount] = React.useState(0)

  // Update expected count when prop changes
  React.useEffect(() => {
    setExpectedCount(expectedGridCount)
  }, [expectedGridCount])

  // Derived states
  const allRegistered = expectedCount > 0 && registeredCount >= expectedCount

  const registerGrid = useCallback((id: number, gridInfo: GridInfo) => {
    gridsRef.current.set(id, gridInfo)
    setRegisteredCount(gridsRef.current.size)
  }, [])

  const unregisterGrid = useCallback((id: number) => {
    gridsRef.current.delete(id)
    setRegisteredCount(gridsRef.current.size)
  }, [])

  const getRegisteredGrids = useCallback(() => {
    return Array.from(gridsRef.current.keys())
  }, [])

  const getGridInfo = useCallback((gridId: number) => {
    return gridsRef.current.get(gridId)
  }, [])

  const exportSingleCSV = useCallback((gridId: number, fileName?: string) => {
    const gridInfo = gridsRef.current.get(gridId)

    if (!gridInfo) {
      return
    }

    const { api, sheetName, exportOptions = {} } = gridInfo

    const rawFileName = fileName || `${sheetName.toLowerCase().replace(/\s+/g, '-')}`
    const sanitizedFileName = sanitizeFilename(rawFileName, 'export', 'csv')
    api.exportDataAsCsv({
      fileName: sanitizedFileName,
      ...defaultCsvOptions,
      ...(exportOptions as Partial<CsvExportParams>)
    })
  }, [])

  const exportSingleExcel = useCallback(
    async (gridId: number, fileName?: string) => {
      const gridInfo = gridsRef.current.get(gridId)

      if (!gridInfo) {
        return
      }

      setIsExporting(true)

      try {
        const { api, sheetName, exportOptions = {}, getCellMetadata } = gridInfo

        const rawFileName = fileName || `${sheetName.toLowerCase().replace(/\s+/g, '-')}`

        // New ExcelJS-based path — used when the grid provides per-cell metadata
        if (getCellMetadata) {
          await exportCustomReportToExcel([{ sheetName, api, getCellMetadata }], rawFileName)
          return
        }

        // Fallback: ag-Grid built-in export
        const sanitizedFileName = sanitizeFilename(rawFileName, 'export', 'xlsx')
        api.exportDataAsExcel({
          fileName: sanitizedFileName,
          sheetName,
          ...defaultExcelOptions,
          ...(exportOptions as Partial<ExcelExportParams>)
        })
      } catch (error) {
        const errorMessage = `Export failed for grid "${gridId}"`
        console.error(errorMessage, error)
        onExportError?.(errorMessage)
      } finally {
        setIsExporting(false)
      }
    },
    [onExportError]
  )

  const sortGridEntries = useCallback((entries: [number, GridInfo][], layout?: Layout[]) => {
    if (!layout?.length) return entries
    const posMap = new Map<number, { x: number; y: number }>()
    layout.forEach(l => posMap.set(Number(l.i), { x: l.x, y: l.y }))
    return [...entries].sort((a, b) => {
      const pa = posMap.get(a[0]) ?? { x: 0, y: Infinity }
      const pb = posMap.get(b[0]) ?? { x: 0, y: Infinity }
      return pa.y !== pb.y ? pa.y - pb.y : pa.x - pb.x
    })
  }, [])

  const exportAllToExcel = useCallback(
    async (fileName = 'raportti.xlsx', layout?: Layout[]) => {
      if (gridsRef.current.size === 0) {
        return
      }

      setIsExporting(true)

      try {
        const sortedEntries = sortGridEntries(Array.from(gridsRef.current.entries()), layout)
        const allGrids = sortedEntries.map(([, g]) => g)
        const allHaveMetadata = allGrids.every(g => !!g.getCellMetadata)

        if (allHaveMetadata) {
          const sheets: ExcelExportSheetInput[] = allGrids.map(g => ({
            sheetName: g.sheetName,
            api: g.api,
            getCellMetadata: g.getCellMetadata!
          }))
          await exportCustomReportToExcel(sheets, fileName)
          return
        }

        // Fallback: ag-Grid built-in multi-sheet exporter
        const sanitizedFileName = sanitizeFilename(fileName, 'report', 'xlsx')
        const spreadsheets: string[] = []

        for (const [gridId, gridInfo] of sortedEntries) {
          const { api, sheetName, exportOptions = {} } = gridInfo

          try {
            const sheetData = api.getSheetDataForExcel({
              sheetName,
              ...defaultExcelOptions,
              ...(exportOptions as Partial<ExcelExportParams>)
            })

            if (sheetData) {
              spreadsheets.push(sheetData)
            }
          } catch (error) {
            try {
              const fallbackData = api.getDataAsCsv({
                ...defaultCsvOptions,
                ...(exportOptions as Partial<CsvExportParams>),
                suppressQuotes: false
              })
              if (fallbackData) {
                spreadsheets.push(fallbackData)
              }
            } catch (fallbackError) {
              const errorMessage = `Excel export failed for grid ${gridId}`
              console.error(errorMessage, fallbackError)
              onExportError?.(errorMessage)
            }
          }
        }

        exportMultipleSheetsAsExcel({
          data: spreadsheets,
          fileName: sanitizedFileName
        })
      } catch (error) {
        const errorMessage = 'Excel export failed'
        console.error(errorMessage, error)
        onExportError?.(errorMessage)
      } finally {
        setIsExporting(false)
      }
    },
    [onExportError, sortGridEntries]
  )

  return (
    <GridExportContext.Provider
      value={{
        registerGrid,
        unregisterGrid,
        exportAllToExcel,
        exportSingleExcel,
        exportSingleCSV,
        getRegisteredGrids,
        getGridInfo,
        isExporting,
        onExportError,
        // Grid registration status
        setExpectedCount,
        expectedCount,
        registeredCount,
        allRegistered
      }}
    >
      {children}
    </GridExportContext.Provider>
  )
}
